import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Avatar, Button, Card, Col, Empty, Flex, Form, Input, Modal, Row, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CompassOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import { RowActions, StatusIcon, TableFoot, actionsColumn, dash, fullName, initialsOf, statusColumn, useTableScrollY } from "../../component/table-cells";
import {
  DOCUMENTS,
  ENTITY_DROPDOWN_SETTINGS,
  MERCHANDISER_LIST,
  MERCH_LIST,
  PHOTO_REPORT,
  POINTS_ADMIN,
  SIDE_NAV,
  TRANSLATION_DOWNLOADER,
  VISIT_PAGE,
} from "../../config/lang";
import { MOVEMENTS } from "../../config/const";
import { call, getAbortable } from "../../util/action";
import { fill, translate } from "../../util/util";

import { movementLabel } from "./movement";

import "./merchandisers.scss";

const PAGE_SIZES = [25, 50, 100];

const SEGMENTS = [
  ["all", "seg_all"],
  ["active", "seg_active"],
  ["inactive", "seg_inactive"],
];

const ORDERING = { name: "first_name" };

const FIELD_COL = { xs: { flex: "0 0 100%" }, md: { flex: "0 0 50%" }, xl: { flex: "0 0 25%" } };
const SEARCH_COL = { xs: { flex: "0 0 100%" }, md: { flex: "0 0 50%" } };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

function buildQuery({ search, movement, supervisor, county, segment, absent, newbie, noPoints, page, size, sort }) {
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));
  if (search) p.set("search", search);
  if (movement) p.set("movement", movement);
  if (supervisor) p.set("supervisor", String(idOf(supervisor)));
  if (county) p.set("county", String(idOf(county)));
  if (segment !== "all") p.set("is_active", segment === "active" ? "true" : "false");
  if (absent) p.set("absent", "true");
  if (newbie) p.set("newbie", "true");
  if (noPoints) p.set("no_points", "true");
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return p.toString();
}

function stateToSearch({ search, movement, supervisor, county, segment, absent, newbie, noPoints, page, size, sort }) {
  const p = new URLSearchParams();
  if (search) p.set("q", search);
  if (movement) p.set("movement", movement);
  if (supervisor) p.set("supervisor", String(idOf(supervisor)));
  if (county) p.set("county", String(idOf(county)));
  if (segment !== "all") p.set("segment", segment);
  if (absent) p.set("absent", "1");
  if (newbie) p.set("newbie", "1");
  if (noPoints) p.set("no_points", "1");
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[0]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString();
}

function searchToState(locationSearch) {
  const p = new URLSearchParams(locationSearch);
  const sort = p.get("sort");
  return {
    search: p.get("q") || "",
    movement: p.get("movement") || "",
    supervisor: p.get("supervisor") || "",
    /* ⚠ No default region. This used to seed the supervisor's own county on first load, which was
       harmless only because the API dropped `?county=` silently — the control looked live and
       narrowed nothing. Now that `MerchandiserWebFilter` backs it, seeding would hide every
       merchandiser whose county is unset (the column is nullable and was never even serialized) and
       the default view could come up empty. A region nobody picked must not hide people. */
    county: p.get("county") || "",
    segment: SEGMENTS.some(([value]) => value === p.get("segment")) ? p.get("segment") : "all",
    absent: p.get("absent") === "1",
    newbie: p.get("newbie") === "1",
    noPoints: p.get("no_points") === "1",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

const MerchList = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";
  const movementLabelOf = key => movementLabel(key, lang);

  const t = key => translate(MERCH_LIST, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);

  const initial = useRef(searchToState(location.search)).current;

  const [search, setSearch] = useState(initial.search);
  const [movement, setMovement] = useState(initial.movement);
  const [supervisor, setSupervisor] = useState(initial.supervisor);
  const [county, setCounty] = useState(initial.county);
  const [segment, setSegment] = useState(initial.segment);
  const [absent, setAbsent] = useState(initial.absent);
  const [newbie, setNewbie] = useState(initial.newbie);
  const [noPoints, setNoPoints] = useState(initial.noPoints);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  const [chosenCounty, setChosenCounty] = useState(null);
  const [chosenSupervisor, setChosenSupervisor] = useState(null);
  const [typed, setTyped] = useState(initial.search);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const query = useMemo(() => buildQuery({ search, movement, supervisor, county, segment, absent, newbie, noPoints, page, size, sort }), [
    search,
    movement,
    supervisor,
    county,
    segment,
    absent,
    newbie,
    noPoints,
    page,
    size,
    sort,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`merchandisers/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
      setSelected([]);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  useEffect(() => {
    const next = stateToSearch({ search, movement, supervisor, county, segment, absent, newbie, noPoints, page, size, sort });
    if (next !== location.search.replace(/^\?/, "")) history.replace({ search: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, movement, supervisor, county, segment, absent, newbie, noPoints, page, size, sort]);

  const narrow = fn => (...args) => {
    setPage(1);
    setSelected([]);
    fn(...args);
  };

  const resetFilters = narrow(() => {
    setTyped("");
    setSearch("");
    setMovement("");
    setSupervisor("");
    setChosenSupervisor(null);
    setCounty("");
    setChosenCounty(null);
    setSegment("all");
    setAbsent(false);
    setNewbie(false);
    setNoPoints(false);
    setSort(null);
  });

  const { labels, remember, nameFor } = useLabelCache();

  const chips = useMemo(() => {
    const out = [];
    if (search) out.push({ key: "q", label: translate(DOCUMENTS, "search", lang), value: search });
    if (movement) out.push({ key: "movement", label: translate(MERCHANDISER_LIST, "movement", lang), value: movementLabelOf(movement) });
    if (supervisor) out.push({ key: "supervisor", label: td("supervisor"), value: nameFor("supervisor", chosenSupervisor ?? supervisor) });
    if (county) out.push({ key: "county", label: td("region"), value: nameFor("county", chosenCounty ?? county) });
    if (absent) out.push({ key: "absent", label: t("flag_absent") });
    if (newbie) out.push({ key: "newbie", label: t("flag_newbie") });
    if (noPoints) out.push({ key: "no_points", label: t("flag_no_points") });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, movement, supervisor, chosenSupervisor, county, chosenCounty, absent, newbie, noPoints, labels, lang]);

  const clearChip = narrow(key => {
    if (key === "q") {
      setTyped("");
      setSearch("");
    } else if (key === "movement") setMovement("");
    else if (key === "supervisor") {
      setSupervisor("");
      setChosenSupervisor(null);
    } else if (key === "county") {
      setCounty("");
      setChosenCounty(null);
    } else if (key === "absent") setAbsent(false);
    else if (key === "newbie") setNewbie(false);
    else if (key === "no_points") setNoPoints(false);
  });

  const patchActive = (row, value) =>
    new Promise(resolve => {
      call({ method: "PATCH", url: `merchandisers/${row.id}/`, post: { is_active: value } }, response => resolve(Boolean(response)));
    });

  const applyLocally = (ids, value) => {
    setRows(prev => prev.map(row => (ids.includes(row.id) ? { ...row, is_active: value } : row)));
  };

  const runChange = async (targets, value) => {
    if (!targets.length) {
      setSelected([]);
      return;
    }
    setBusy(true);
    const results = await Promise.all(targets.map(row => patchActive(row, value)));
    setBusy(false);
    const done = targets.filter((row, i) => results[i]);
    if (done.length) {
      applyLocally(
        done.map(row => row.id),
        value
      );
      Alert.success(fill(t(value ? "activated_msg" : "deactivated_msg"), { n: done.length }), { position: "top-right" });
    }
    if (done.length < targets.length) {
      Alert.warning(fill(t("change_failed_msg"), { n: targets.length - done.length }), { position: "top-right" });
    }
    setSelected([]);
  };

  const changeSelected = value => {
    const targets = rows.filter(row => selected.includes(row.id) && row.is_active !== value);
    if (value) {
      runChange(targets, true);
      return;
    }
    Modal.confirm({
      title: fill(t("confirm_deactivate"), { n: targets.length }),
      okText: t("deactivate"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () => runChange(targets, false),
    });
  };

  const openMerch = row => history.push(`/merchandiser/${row.id}`);

  const movementOptions = useMemo(
    () =>
      Object.keys(MOVEMENTS).map(key => ({
        value: key,
        label: movementLabelOf(key),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );

  const dShort = d => {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    return date.toLocaleDateString(lang === "uk" ? "uk-UA" : lang === "hy" ? "hy-AM" : lang === "ka" ? "ka-GE" : "en-GB", {
      day: "2-digit",
      month: "short",
    });
  };

  const dOnly = d => {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    return date.toLocaleDateString(lang === "uk" ? "uk-UA" : lang === "hy" ? "hy-AM" : lang === "ka" ? "ka-GE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const columns = useMemo(
    () => [
      statusColumn((value, row) => (
        <StatusIcon
          Icon={row.is_active ? CheckCircleOutlined : MinusCircleOutlined}
          tone={row.is_active ? "ok" : "off"}
          label={t(row.is_active ? "status_active" : "status_inactive")}
        />
      )),
      {
        key: "name",
        title: translate(POINTS_ADMIN, "f_name", lang),
        width: 260,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => {
          const name = fullName(row);
          return (
            <div className="mc-who">
              <Avatar size={28} className="lp-who__av" style={{ fontSize: 12 }} src={row.photo ?? null}>
                {initialsOf(name)}
              </Avatar>
              <div className="mc-who__info">
                <Typography.Text strong ellipsis={{ tooltip: name }}>
                  {name || dash}
                </Typography.Text>
                {row.phone_number && <span className="mc-who__phone">{row.phone_number}</span>}
              </div>
            </div>
          );
        },
      },
      {
        key: "availability",
        title: t("availability"),
        width: 200,
        render: (value, row) => {
          if (!row.is_active) {
            return (
              <span className="mc-pill mc-pill--off">
                <MinusCircleOutlined className="mc-pill__icon" />
                {t("status_inactive")}
              </span>
            );
          }
          const away = row.current_absence;
          const next = row.next_absence;
          const isNew = row.is_newbie;
          return (
            <div className="mc-avail">
              {away ? (
                <span className="mc-pill mc-pill--warn">
                  <CalendarOutlined className="mc-pill__icon" />
                  {t("away_until")} {dShort(away.end)}
                </span>
              ) : (
                <span className="mc-pill mc-pill--ok">
                  <CheckCircleOutlined className="mc-pill__icon" />
                  {t("available")}
                </span>
              )}
              {!away && next && (
                <span className="mc-tag">
                  <CalendarOutlined className="mc-tag__icon" />
                  {dShort(next.start)}
                </span>
              )}
              {isNew && <span className="mc-tag mc-tag--new">{t("newbie")}</span>}
            </div>
          );
        },
      },
      {
        key: "movement",
        title: translate(MERCHANDISER_LIST, "movement", lang),
        width: 170,
        render: (value, row) => {
          if (!row.movement) return dash;
          return (
            <span className="mc-mv">
              <CarOutlined className="mc-mv__icon" />
              {movementLabelOf(row.movement)}
            </span>
          );
        },
      },
      /* No coverage COUNT and no 7-day visit COUNT — both were per-row aggregates that Django 3.1
         re-runs inside the list's pagination `COUNT(*)` (see CLAUDE.md). The "no points" question
         is still askable, and better: it is the chip in the filter row, which the server answers
         with an `Exists`. `last_visit` replaces the visit count — one indexed subquery, and the
         date answers "are they working" more directly than a 7-day total.
         Neither is sortable: `ordering` accepts only what `ORDERING` maps. */
      {
        key: "last_visit",
        title: t("last_visit"),
        dataIndex: "last_visit",
        width: 130,
        align: "right",
        render: value => (value ? <span className="lp-num">{dShort(value)}</span> : <Typography.Text type="secondary">{t("never_visited")}</Typography.Text>),
      },
      {
        key: "supervisor_name",
        title: td("supervisor"),
        width: 170,
        ellipsis: true,
        render: (value, row) => row.supervisor_name || dash,
      },
      {
        key: "tenure",
        title: t("tenure"),
        width: 140,
        render: (value, row) => {
          if (!row.date_joined) return dash;
          const joined = new Date(row.date_joined);
          const days = Math.round((Date.now() - joined.getTime()) / 864e5);
          const years = Math.floor(days / 365);
          return (
            <Flex vertical className="lp-main">
              <Typography.Text strong className="lp-num">
                {years >= 1 ? `${years} ${t("yr_short")}` : `${days} ${t("days_short")}`}
              </Typography.Text>
              <Typography.Text type="secondary" className="lp-main__sub">
                {dOnly(row.date_joined)}
              </Typography.Text>
            </Flex>
          );
        },
      },
      actionsColumn((value, row) => (
        <RowActions
          label={tv("row_actions")}
          items={[
            { key: "open", icon: <ExportOutlined />, label: t("open") },
            { key: "absence", icon: <CalendarOutlined />, label: t("add_absence") },
            row.is_active
              ? { key: "deactivate", icon: <MinusCircleOutlined />, label: t("deactivate"), danger: true }
              : { key: "activate", icon: <CheckCircleOutlined />, label: t("activate") },
          ]}
          onClick={key => {
            if (key === "open") openMerch(row);
            else if (key === "absence") history.push(`/merchandiser/${row.id}`);
            else if (key === "activate") runChange([row], true);
            else
              Modal.confirm({
                title: fill(t("confirm_deactivate"), { n: 1 }),
                okText: t("deactivate"),
                okButtonProps: { danger: true },
                cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
                onOk: () => runChange([row], false),
              });
          }}
        />
      )),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, rows]
  );

  const scrollY = useTableScrollY();

  return (
    <Flex vertical gap="middle" className="shell-content lp mc">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "merchandiser", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/merchandiser/new")}>
          {t("new_merch")}
        </Button>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...SEARCH_COL}>
              <Form.Item label={translate(DOCUMENTS, "search", lang)}>
                <Input allowClear prefix={<SearchOutlined />} placeholder={t("search_ph")} value={typed} onChange={event => setTyped(event.target.value)} />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={translate(MERCHANDISER_LIST, "movement", lang)}>
                <Select
                  allowClear
                  placeholder={translate(MERCHANDISER_LIST, "movement", lang)}
                  value={movement || undefined}
                  options={movementOptions}
                  onChange={narrow(v => setMovement(v || ""))}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("supervisor")}>
                <EntitySelect
                  entity="supervisor"
                  value={supervisor}
                  seed={chosenSupervisor}
                  prefix={<TeamOutlined />}
                  onChange={narrow(option => {
                    setChosenSupervisor(option);
                    setSupervisor(option ? idOf(option) : "");
                  })}
                  onResolve={remember("supervisor")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("region")}>
                <EntitySelect
                  entity="county"
                  value={county}
                  seed={chosenCounty || (me?.county && typeof me.county === "object" ? me.county : null)}
                  prefix={<CompassOutlined />}
                  onChange={narrow(option => {
                    setChosenCounty(option);
                    setCounty(option ? idOf(option) : "");
                  })}
                  onResolve={remember("county")}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented value={segment} onChange={narrow(setSegment)} options={SEGMENTS.map(([value, key]) => ({ value, label: t(key) }))} />
            <Tag.CheckableTag className="mc-chip" checked={absent} onChange={narrow(setAbsent)}>
              <Space size={6}>
                <CalendarOutlined />
                {t("flag_absent")}
              </Space>
            </Tag.CheckableTag>
            <Tag.CheckableTag className="mc-chip" checked={newbie} onChange={narrow(setNewbie)}>
              {t("flag_newbie")}
            </Tag.CheckableTag>
            <Tag.CheckableTag className="mc-chip" checked={noPoints} onChange={narrow(setNoPoints)}>
              {t("flag_no_points")}
            </Tag.CheckableTag>
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "merchandiser", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1100, y: scrollY }}
          rowClassName={row => (selected.includes(row.id) ? "lp-row lp-row--on" : "lp-row")}
          rowSelection={{ fixed: true, selectedRowKeys: selected, onChange: keys => setSelected(keys) }}
          onRow={row => ({
            onClick: event => {
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn")) return;
              openMerch(row);
            },
          })}
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>{t("empty_title")}</Typography.Text>
                    <Typography.Text type="secondary">{t("empty_desc")}</Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="mc"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
          onPage={next => {
            setPage(next);
            setSelected([]);
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            setSelected([]);
          }}
        />
      </Card>

      {selected.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: selected.length })}</Typography.Text>
          <Space size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {tp("clear_selection")}
            </Button>
            <Button size="small" icon={<CheckCircleOutlined />} loading={busy} onClick={() => changeSelected(true)}>
              {t("activate")}
            </Button>
            <Button size="small" danger type="primary" icon={<MinusCircleOutlined />} loading={busy} onClick={() => changeSelected(false)}>
              {t("deactivate")}
            </Button>
          </Space>
        </Flex>
      )}
    </Flex>
  );
};

export default MerchList;
